import {
  Grid,
  MenuItem,
  TextField,
} from "@material-ui/core";
import * as React from "react";
import SearchPrescriptionSection from "./prescription/SearchPrescriptionSection";
import CompleteFillSection from "./fill/CompleteFillSection";
import ListPrescriptionsSection from "./prescription/ListPrescriptionsSection";
import EnableAutoRefillSection from "./auto_refill/EnableAutoRefillSection";
import DisableAutoRefillSection from "./auto_refill/DisableAutoRefillSection";
import CreateAutoRefillOrderSection from "./auto_refill/CreateAutoRefillOrderSection";
import UpdatePrescriptionSection from "./prescription/UpdatePrescriptionSection";
import RxEnhancementForHOASection from "./prescription/RxEnhancementForHOASection";
import { Alert } from "@material-ui/lab";
import OverrideConsumptionGuardSection from "./prescription/OverrideConsumptionGuardSection";
import UpdateDateWrittenSection from "./prescription/UpdateDateWrittenSection";
import CcsStatusUpdateSection from "./prescription/CcsStatusUpdateSection";
import GetCcsTaskIdSection from "./prescription/GetCcsTaskIdSection";

enum PrescriptionOperation {
  CompleteFill = 'CompleteFill',
  CreateAutoRefillOrder = 'CreateAutoRefillOrder',
  DisableAutoRefill = 'DisableAutoRefill',
  EnableAutoRefill = 'EnableAutoRefill',
  ListPrescription = 'ListPrescription',
  OverrideConsumption = 'OverrideConsumption',
  RxEnhancements = 'RxEnhancements',
  SearchPrescription = 'SearchPrescription',
  UpdateDateWritten = 'UpdateDateWritten',
  UpdatePrescription = 'UpdatePrescription',
  CcsStatusUpdate = 'CcsStatusUpdate',
  GetCcsTaskId = 'GetCcsTaskId',
}

const OPERATION_LABELS: Record<PrescriptionOperation, string> = {
  [PrescriptionOperation.ListPrescription]: 'List Prescriptions',
  [PrescriptionOperation.SearchPrescription]: 'Search Prescription',
  [PrescriptionOperation.UpdatePrescription]: 'Update Prescription',
  [PrescriptionOperation.CompleteFill]: 'Complete Fill',
  [PrescriptionOperation.EnableAutoRefill]: 'Enable Auto Refill',
  [PrescriptionOperation.DisableAutoRefill]: 'Disable Auto Refill',
  [PrescriptionOperation.CreateAutoRefillOrder]: 'Create Auto Refill Order',
  [PrescriptionOperation.RxEnhancements]: 'Rx Enhancement for HOA',
  [PrescriptionOperation.OverrideConsumption]: 'Override Consumption',
  [PrescriptionOperation.UpdateDateWritten]: 'Update Date Written',
  [PrescriptionOperation.CcsStatusUpdate]: 'CCS Status Update',
  [PrescriptionOperation.GetCcsTaskId]: 'CCS Deep Links',
};

interface IPrescriptionSectionState {
  selectedOperation: PrescriptionOperation | '';
}

// Routed component; only the router-provided location is read (for deep-link query params).
interface IPrescriptionSectionProps {
  location?: { search?: string };
}

class PrescriptionLifecycleSection extends React.Component<
  IPrescriptionSectionProps,
  IPrescriptionSectionState
> {
  private readonly initialPersonId: string;
  private readonly autoSubmit: boolean;
  private autoSubmitConsumed = false;

  constructor(props: IPrescriptionSectionProps) {
    super(props);

    // Supports deep-linking via ?operation=<PrescriptionOperation>&personId=<id>
    // (e.g. the "Next Steps" links on the NewRx ingestion workflow). Keep the
    // operation values in sync with the deep-links in UnifiedNewRxWorkflowSection.
    const params = new URLSearchParams(props.location?.search || '');
    const requestedOperation = params.get('operation') || '';
    const isKnownOperation = (Object.values(PrescriptionOperation) as string[])
      .includes(requestedOperation);
    this.initialPersonId = params.get('personId') || '';
    this.autoSubmit = params.get('autoSubmit') === 'true';

    this.state = {
      selectedOperation: isKnownOperation
        ? (requestedOperation as PrescriptionOperation)
        : '',
    };
  }

  // Children mount before parents, so by the time this runs the child section has
  // already consumed the one-time auto-submit. Mark it spent so that toggling the
  // operation dropdown away and back (which remounts the section) does not re-fire
  // the auto-submitted mutation.
  public componentDidMount() {
    this.autoSubmitConsumed = true;
  }

  public render() {
    return (
      <div style={{ flexGrow: 1 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3} justify="center">
              <Grid item xs>
                <Alert severity="info">
                  <p>
                    For more information on how to use this page please visit
                    our &nbsp;
                    <a
                      href="https://w.amazon.com/bin/view/Pharmacy/Public/Teams/APAQ/Automation/TestTools/Portal/Prescriptions/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Wiki page
                    </a>
                    !
                  </p>
                </Alert>

              </Grid>
            </Grid>
            <Grid container spacing={3} justify="center">
              <Grid item xs={6}>
                <TextField
                    select
                    fullWidth
                    label="Select Operation"
                    variant="outlined"
                    value={this.state.selectedOperation}
                    onChange={(e) => this.setState({ selectedOperation: e.target.value as PrescriptionOperation })}
                >
                  {Object.values(PrescriptionOperation).map((op) => (
                      <MenuItem key={op} value={op}>
                        {OPERATION_LABELS[op]}
                      </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Grid>

          {this.state.selectedOperation && (
              <Grid item xs={12}>
                {this.renderSelectedOperation()}
              </Grid>
          )}
        </Grid>
      </div>
    );
  }

  private renderSelectedOperation() {
    switch (this.state.selectedOperation) {
      case PrescriptionOperation.ListPrescription: return <ListPrescriptionsSection />;
      case PrescriptionOperation.SearchPrescription: return <SearchPrescriptionSection />;
      case PrescriptionOperation.UpdatePrescription: return <UpdatePrescriptionSection />;
      case PrescriptionOperation.CompleteFill: return <CompleteFillSection />;
      case PrescriptionOperation.EnableAutoRefill: return <EnableAutoRefillSection />;
      case PrescriptionOperation.DisableAutoRefill: return <DisableAutoRefillSection />;
      case PrescriptionOperation.CreateAutoRefillOrder: return <CreateAutoRefillOrderSection />;
      case PrescriptionOperation.RxEnhancements: return <RxEnhancementForHOASection />;
      case PrescriptionOperation.OverrideConsumption: return <OverrideConsumptionGuardSection />;
      case PrescriptionOperation.UpdateDateWritten: return <UpdateDateWrittenSection />;
      case PrescriptionOperation.CcsStatusUpdate: return <CcsStatusUpdateSection initialPersonId={this.initialPersonId} autoSubmit={this.autoSubmit && !this.autoSubmitConsumed} mode="either" />;
      case PrescriptionOperation.GetCcsTaskId: return <GetCcsTaskIdSection />;
      default: return null;
    }
  }
}

export default PrescriptionLifecycleSection;
